import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Send, User, FileText, Award, Calendar, Mic, MicOff } from 'lucide-react';

const TherapistQuestionnairePage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [therapistInfo, setTherapistInfo] = useState({
    name: '',
    age: '',
    experience: ''
  });
  const [responses, setResponses] = useState({});
  const [questions, setQuestions] = useState([]);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [therapistProfile, setTherapistProfile] = useState(null);
  
  const textareaRef = useRef(null);
  const navigate = useNavigate();

  // Add these imports at the top (if not already present)

// Add these new state variables and ref
const [isListening, setIsListening] = useState(false);
const [transcript, setTranscript] = useState('');
const recognitionRef = useRef(null);

useEffect(() => {
    // When currentStep changes, reset the textarea value based on the current question's response
    if (textareaRef.current && currentStep > 0 && currentStep <= questions.length) {
      const questionId = questions[currentStep - 1].id;
      textareaRef.current.value = responses[questionId] || '';
    }
  }, [currentStep, questions, responses]);

// Add this effect to stop recording when changing questions
useEffect(() => {
  // If we have an active recognition instance, stop it
  if (recognitionRef.current) {
    recognitionRef.current.stop();
    setIsListening(false);
  }
  
  // Clean up function to ensure speech recognition is stopped
  return () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };
}, [currentStep]);

// Add this toggleVoiceInput function
const toggleVoiceInput = (e) => {
    // Prevent default focus behavior
    e.preventDefault();
    e.stopPropagation();
    
    console.log("Voice button clicked");
    
    // If already listening, stop it
    if (isListening && recognitionRef.current) {
      console.log("Stopping speech recognition");
      
      // Store the current text before stopping
      const currentText = textareaRef.current?.value || '';
      const questionId = questions[currentStep - 1].id;
      
      // First stop the recognition
      recognitionRef.current.stop();
      setIsListening(false);
      
      // Then ensure the text is preserved in the textarea
      // Use a small timeout to let the recognition fully stop
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.value = currentText;
        }
      }, 10);
      
      return;
    }
    
    // Check if browser supports speech recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      alert("Your browser doesn't support speech recognition. Please try a different browser.");
      return;
    }
    
    try {
      // Get current text before creating recognition instance
      const existingText = textareaRef.current?.value || '';
      
      // Create a new recognition instance each time
      recognitionRef.current = new SpeechRecognition();
      const recognition = recognitionRef.current;
      
      // Configure recognition
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US'; // Set language explicitly
      
      // Track the accumulated transcript in a variable that persists between events
      let finalTranscript = existingText;
      
      // Set up event handlers
      recognition.onstart = () => {
        console.log("Speech recognition started");
        setIsListening(true);
      };
      
      recognition.onresult = (event) => {
        console.log("Speech recognition result received");
        
        // Get the most recent result
        let interimTranscript = '';
        
        // Process results
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          
          if (event.results[i].isFinal) {
            // For final results, append to final transcript
            finalTranscript += ' ' + transcript;
          } else {
            // For interim results, add to interim transcript
            interimTranscript += transcript;
          }
        }
        
        // Combine final transcript (saved results) with interim (in-progress results)
        const currentTranscript = (finalTranscript + ' ' + interimTranscript).trim();
        
        // Set for accessibility feedback
        setTranscript(currentTranscript);
        
        // Update textarea directly
        if (textareaRef.current) {
          textareaRef.current.value = currentTranscript;
        }
      };
      
      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);
        
        if (event.error === 'not-allowed') {
          alert("Microphone permission was denied. Please allow microphone access in your browser settings.");
        } else {
          alert(`Speech recognition error: ${event.error}`);
        }
      };
      
      recognition.onend = () => {
        console.log("Speech recognition ended");
        
        // IMPORTANT CHANGE: Only preserve text if we haven't navigated to another step
        // This allows the reset mechanism to work when navigating
        const sameStep = true; // This is a simplification - in reality navigation handlers override this
        
        // When speech ends by itself, preserve the current text only if still on same step
        if (sameStep && textareaRef.current) {
          const currentText = textareaRef.current.value || '';
          
          // Make sure the text stays in the textarea after speech ends
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.value = currentText;
            }
          }, 10);
        }
        
        setIsListening(false);
      };
      
      // This will trigger the permission dialog
      console.log("Starting speech recognition");
      recognition.start();
      
    } catch (error) {
      console.error("Error initializing speech recognition:", error);
      setIsListening(false);
      alert(`Failed to start speech recognition: ${error.message}`);
    }
  };

  useEffect(() => {
    // After currentStep changes, focus the textarea if available
    if (textareaRef.current && currentStep > 0 && currentStep <= questions.length) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        textareaRef.current.focus();
      }, 50);
    }
  }, [currentStep, questions.length]);
  // Fetch therapist questions when component mounts
  useEffect(() => {
    fetchTherapistQuestions();
  }, []);
  
  // Function to fetch therapist-specific questions from the server
  const fetchTherapistQuestions = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/get-therapist-questions');
      
      if (!response.ok) {
        throw new Error('Failed to fetch therapist questions');
      }
      
      const data = await response.json();
      
      if (data.success && data.questions && data.questions.length > 0) {
        console.log('Loaded therapist questions:', data.questions);
        setQuestions(data.questions);
      } else {
        // No questions available
        console.log('No therapist questions available');
        setQuestions([]);
      }
      
      setQuestionsLoaded(true);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching therapist questions:', error);
      setQuestions([]);
      setQuestionsLoaded(true);
      setLoading(false);
    }
  };

  // Handle input change for therapist info fields
  const handleInfoChange = (e) => {
    const { name, value } = e.target;
    setTherapistInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle input change for question responses
  const handleResponseChange = (questionId, value) => {
    // This will only be called on Next/Previous navigation, not on every keystroke
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // Handle moving to next step
  const handleNext = () => {
  // Validate current step
  if (currentStep === 0) {
    // Validate therapist info (basic info step)
    if (!therapistInfo.name.trim()) {
      alert('Please enter your name');
      return;
    }
  } else if (currentStep <= questions.length) {
    // Validate question response by reading directly from textarea
    const questionId = questions[currentStep - 1].id;
    
    // Read current value directly from textarea if available
    const currentValue = textareaRef.current ? textareaRef.current.value.trim() : responses[questionId] || '';
    
    if (!currentValue) {
      alert('Please provide an answer before continuing');
      return;
    }
    
    // Store the current value in the responses state
    setResponses(prev => ({
      ...prev,
      [questionId]: currentValue
    }));
  }
  
  // Important: If speech recognition is active, we need special handling
  if (isListening && recognitionRef.current) {
    // Flag to indicate we're moving to next question
    const movingToNext = true;
    
    // Stop the recognition
    recognitionRef.current.stop();
    
    // Remove any existing onend handler to prevent it from interfering
    if (recognitionRef.current.onend) {
      const oldOnEndHandler = recognitionRef.current.onend;
      recognitionRef.current.onend = () => {
        setIsListening(false);
        // After stopping, then move to next step
        setCurrentStep(currentStep + 1);
      };
      
      return; // Exit early - the step change will happen in the onend handler
    }
  }
  
  // If no speech recognition is active, or we handled it above,
  // move to the next step directly
  setCurrentStep(currentStep + 1);
};

// Updated handlePrevious function with similar changes
const handlePrevious = () => {
  if (currentStep > 0) {
    // Save current response if on a question step
    if (currentStep > 0 && currentStep <= questions.length && textareaRef.current) {
      const questionId = questions[currentStep - 1].id;
      const currentValue = textareaRef.current.value;
      
      setResponses(prev => ({
        ...prev,
        [questionId]: currentValue
      }));
    }
    
    // If speech recognition is active, we need special handling
    if (isListening && recognitionRef.current) {
      // Stop the recognition
      recognitionRef.current.stop();
      
      // Remove any existing onend handler to prevent it from interfering
      if (recognitionRef.current.onend) {
        const oldOnEndHandler = recognitionRef.current.onend;
        recognitionRef.current.onend = () => {
          setIsListening(false);
          // After stopping, then move to previous step
          setCurrentStep(currentStep - 1);
        };
        
        return; // Exit early - the step change will happen in the onend handler
      }
    }
    
    // If no speech recognition active, move to previous step directly
    setCurrentStep(currentStep - 1);
  }
};
  
  // Handle final submission
  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Prepare responses data
      const responsesData = Object.keys(responses).map(questionId => ({
        questionId,
        question: questions.find(q => q.id === questionId)?.text || "Unknown Question",
        answer: responses[questionId]
      }));
      
      // Submit all data to server
      const response = await fetch('/api/submit-therapist-responses', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          therapistName: therapistInfo.name,
          age: therapistInfo.age,
          experience: therapistInfo.experience,
          responses: responsesData
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit therapist responses');
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Set the therapist profile from the response
        setTherapistProfile(data.therapist);
        setComplete(true);
        
        // Save responses individually to database
        await saveResponsesToDatabase(responsesData);
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Error submitting therapist questionnaire:', error);
      alert('There was an error submitting your responses. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to save individual responses
  const saveResponsesToDatabase = async (responsesData) => {
    try {
      // Get user from localStorage
      const storedUser = localStorage.getItem('user');
      if (!storedUser) return;
      
      const userData = JSON.parse(storedUser);
      
      // Save each response
      for (const response of responsesData) {
        await fetch('/api/save-therapist-response', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: userData.id,
            questionId: response.questionId,
            question: response.question,
            answer: response.answer
          }),
        });
      }
      
      console.log('All responses saved to database');
    } catch (error) {
      console.error('Error saving responses to database:', error);
    }
  };
  
  // Get current content based on step
  const renderCurrentStep = () => {
    if (currentStep === 0) {
      // Therapist info page
      return (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Tell Us About Yourself
          </h2>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Your Name*
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={therapistInfo.name}
                  onChange={handleInfoChange}
                  className="pl-10 w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Dr. Jane Smith"
                  required
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-1">
                Your Age (Optional)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  id="age"
                  name="age"
                  value={therapistInfo.age}
                  onChange={handleInfoChange}
                  className="pl-10 w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="35"
                  min="18"
                  max="100"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-1">
                Years of Experience*
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Award className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  id="experience"
                  name="experience"
                  value={therapistInfo.experience}
                  onChange={handleInfoChange}
                  className="pl-10 w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="10"
                  min="0"
                  max="70"
                  required
                />
              </div>
            </div>
          </div>
          
          <p className="text-sm text-gray-500 italic">
            This information helps us create your therapist profile for our matching system.
          </p>
        </div>
      );
    } else if (currentStep <= questions.length) {
      // Question pages
      const question = questions[currentStep - 1];
      return (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {question.text}
          </h2>
          
          {question.source && (
            <p className="text-xs text-gray-500 mb-2">
              Contributed by Dr. {question.source.therapist.charAt(0).toUpperCase() + question.source.therapist.slice(1)}
            </p>
          )}
          
          {question.reasoning && (
            <p className="text-sm text-blue-600 italic mb-4">
              Why we're asking: {question.reasoning}
            </p>
          )}
          
         {/* Only add the microphone to question steps, not the personal info step */}
         {currentStep > 0 && currentStep <= questions.length ? (
  <div className="relative">
    <textarea
      ref={textareaRef}
      className="w-full p-3 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-32"
      placeholder="Type your answer here..."
      defaultValue={responses[question.id] || ''}
      // Removed onChange handler - we'll update state on navigation, not on each keystroke
    ></textarea>
    
    <button 
      type="button"
      onMouseDown={(e) => {
        // Prevent focus from leaving textarea on mousedown
        e.preventDefault();
      }}
      onClick={(e) => toggleVoiceInput(e)}
      className={`absolute bottom-3 right-3 p-2 rounded-full shadow-sm hover:shadow ${
        isListening 
          ? 'bg-red-100 text-red-600 animate-pulse ring-2 ring-red-400' 
          : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
      }`}
      title={isListening ? "Stop recording" : "Answer with voice"}
    >
      {isListening ? <Mic size={20} /> : <Mic size={20} />}
    </button>
  </div>
) : (
        /* Keep the original input for personal info fields */
        <textarea
            ref={textareaRef}
            className="w-full p-3 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-32"
            placeholder="Type your answer here..."
            defaultValue={responses[question.id] || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            ></textarea>
        )}
        </div>
      );
    } else {
      // Review page
      return (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Review Your Responses
          </h2>
          
          <div className="border border-gray-200 rounded-lg p-4 mb-4">
            <h3 className="font-medium text-gray-700">Your Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p>{therapistInfo.name}</p>
              </div>
              {therapistInfo.age && (
                <div>
                  <p className="text-sm text-gray-500">Age</p>
                  <p>{therapistInfo.age}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500">Years of Experience</p>
                <p>{therapistInfo.experience}</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <h3 className="font-medium text-gray-700">Your Questionnaire Responses</h3>
            
            {questions.map((question, index) => (
              <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                <p className="font-medium text-gray-700">Question {index + 1}: {question.text}</p>
                <p className="mt-2 whitespace-pre-wrap">{responses[question.id]}</p>
              </div>
            ))}
          </div>
          
          <p className="text-sm text-gray-500 italic">
            Please review your responses before submitting. Your answers will help us create your therapist profile in our matching system.
          </p>
        </div>
      );
    }
  };
  
  // Get current progress percentage
  const getProgressPercentage = () => {
    const totalSteps = questions.length + 2; // Info page + questions + review page
    return (currentStep / (totalSteps - 1)) * 100;
  };
  
  // Get current step label
  const getStepLabel = () => {
    if (currentStep === 0) {
      return "Therapist Information";
    } else if (currentStep <= questions.length) {
      return `Question ${currentStep} of ${questions.length}`;
    } else {
      return "Review & Submit";
    }
  };
  
  // Render completion page
  const renderCompletionPage = () => {
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="text-green-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">Profile Created!</h2>
          <p className="text-gray-600 mt-2">Your therapist profile has been successfully created.</p>
        </div>
        
        {therapistProfile && (
          <div className="border border-green-200 bg-green-50 rounded-lg p-6 mb-6">
            <h3 className="text-xl font-semibold text-green-800 mb-4">Your Therapist Profile</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">{therapistProfile.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Experience</p>
                <p className="font-medium">{therapistProfile.experience} years</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm text-gray-500">Specialty</p>
                <p className="font-medium">{therapistProfile.specialty}</p>
              </div>
            </div>
            
            <div className="mt-4">
              <p className="text-sm text-gray-500 mb-2">Therapeutic Style</p>
              <p className="text-gray-700">{therapistProfile.style}</p>
            </div>
            
            {therapistProfile.tags && therapistProfile.tags.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-2">Key Characteristics</p>
                <div className="flex flex-wrap gap-2">
                  {therapistProfile.tags.map((tag, index) => (
                    <span key={index} className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {therapistProfile && therapistProfile.insightsApplied && therapistProfile.insightsApplied.length > 0 && (
        <div className="mt-6 border border-purple-200 bg-purple-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-purple-800 mb-3">Expert Insights Applied</h3>
          <div className="space-y-4">
            {therapistProfile.insightsApplied.map((insight, index) => (
              <div key={index} className="p-3 bg-white rounded-md shadow-sm">
                <div className="font-medium text-purple-700">
                  From Dr. {insight.source.charAt(0).toUpperCase() + insight.source.slice(1)}:
                </div>
                <div className="mt-1 text-gray-700">"{insight.insightText}"</div>
                <div className="mt-2 text-sm text-gray-600">
                  <span className="font-medium">Applied to your profile:</span> {insight.relevance}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {therapistProfile.description && (
        <div className="mt-6 border border-blue-200 bg-blue-50 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-blue-800 mb-4">Your Therapeutic Approach</h3>
          <div className="prose prose-blue max-w-none">
            {therapistProfile.description.split('\n\n').map((paragraph, i) => (
              <p key={i} className="mb-4 last:mb-0">{paragraph}</p>
            ))}
          </div>
        </div>
      )}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button 
            onClick={() => navigate('/therapist-dashboard')}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Go to Your Dashboard
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-blue-50 flex flex-col">
      {/* Header */}
      <header className="bg-blue-600 text-white p-4 shadow-md">
        <div className="container mx-auto flex items-center">
          <FileText className="mr-2" size={28} />
          <h1 className="text-xl md:text-2xl font-bold">Therapist Questionnaire</h1>
          <span className="ml-3 bg-blue-800 text-xs px-2 py-1 rounded-full">Mindaera</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow container mx-auto py-8 px-4">
        {!questionsLoaded ? (
          // Loading state while fetching questions
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6 flex justify-center">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              <p className="mt-4 text-gray-600">Loading therapist questionnaire...</p>
            </div>
          </div>
        ) : questions.length === 0 ? (
          // No questions available state
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="rounded-full bg-yellow-100 w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <span className="text-yellow-600 text-2xl">!</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">No Therapist Questions Available</h2>
            <p className="text-gray-600 mb-4">
              There are currently no questions for therapists in our system. Please check back later.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Return to Home
            </button>
          </div>
        ) : !complete ? (
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="h-2 bg-blue-100 rounded-full">
                <div 
                  className="h-full bg-blue-600 rounded-full transition-all duration-300" 
                  style={{ width: `${getProgressPercentage()}%` }}
                ></div>
              </div>
              <div className="mt-2 text-sm text-blue-600 font-medium">
                {getStepLabel()}
              </div>
            </div>

            {/* Current Step Content */}
            {renderCurrentStep()}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              <button
                onClick={handlePrevious}
                disabled={currentStep === 0 || loading}
                className={`flex items-center px-4 py-2 rounded-md ${
                  currentStep === 0 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                <ChevronLeft size={18} className="mr-1" />
                Previous
              </button>
              
              {currentStep < questions.length + 1 ? (
                <button
                  onClick={handleNext}
                  disabled={loading}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    <>
                      Next
                      <ChevronRight size={18} className="ml-1" />
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-300"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></div>
                      Submitting...
                    </div>
                  ) : (
                    <>
                      Submit Profile
                      <Send size={18} className="ml-1" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        ) : (
          // Completion page
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6">
            {renderCompletionPage()}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-blue-800 text-blue-200 py-4 text-center text-sm">
        <div className="container mx-auto">
          Mindaera | Internal Dev Prototype | Not for public release
        </div>
      </footer>
    </div>
  );
};

export default TherapistQuestionnairePage;